import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth, invalidateCachedFetch } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import { GymDiscovery } from '../features/join/GymDiscovery'
import { GymMark } from '../features/nav/GymMark'
import { Page } from '../design/Page'
import { Section } from '../design/Section'
import { SurfaceCard } from '../design/SurfaceCard'
import { SkeletonCard } from '../design/Skeleton'
import { useMyGyms } from '../features/gym/useMyGyms'

interface PendingRequest {
  gym_id: string
  gym_name: string | null
  status: 'pending' | 'approved'
}

export function OurGymsPage() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // Shared with the switcher and the nav, so a plan sold at the desk adds the
  // gym here the moment the realtime listener invalidates the key.
  const { gyms, loading: gymsLoading } = useMyGyms()
  const [preferredGymId, setPreferredGymId] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingRequest[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const [profileRes, joinRes] = await Promise.all([
          fetch(`${apiUrl}/my-profile`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/my-join-request`, { headers: { Authorization: `Bearer ${token}` } }),
        ])
        if (joinRes.ok) {
          const data = (await joinRes.json()) as { requests?: PendingRequest[] }
          setPending((data.requests ?? []).filter((r) => r.status === 'pending'))
        }
        if (profileRes.ok) {
          const data = (await profileRes.json()) as { preferredGymId: string | null }
          setPreferredGymId(data.preferredGymId)
        }
      } catch {
        // The gym list comes from the shared cache; only the extras above can
        // fail here, and they degrade to empty.
      }
    }
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  async function handleSetDefault(newGymId: string) {
    if (newGymId === preferredGymId) return
    setError(null)
    const previous = preferredGymId
    setPreferredGymId(newGymId)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferredGymId: newGymId }),
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setPreferredGymId(previous)
        setError(data.error ?? t('common.errorDefault'))
      } else {
        // The landing gym is read from the cached profile, and it decides where
        // Home sends you next time.
        invalidateCachedFetch('my-profile')
      }
    } catch {
      setPreferredGymId(previous)
      setError(t('common.networkError'))
    }
  }

  if (gymsLoading) {
    return (
      <Page>
        <SkeletonCard lines={2} />
        <SkeletonCard lines={2} />
      </Page>
    )
  }

  // Mirrors GymRedirect: the landing gym is the explicit preference, else the first gym.
  const effectiveDefaultId = preferredGymId ?? gyms[0]?.id

  return (
    <Page>
      <h1 className="sr-only">{t('ourGyms.pageTitle')}</h1>

      {gyms.length > 0 && (
        <Section title={t('ourGyms.myGymsLabel')}>
          <div className="flex flex-col gap-2">
            {gyms.map((g) => {
              const isDefault = g.id === effectiveDefaultId
              return (
                <div key={g.id} className="flex items-center gap-2">
                  <SurfaceCard
                    isPressable
                    onClick={() => void navigate(`/${g.slug}/dashboard`)}
                    className="flex flex-1 flex-row items-center gap-3 p-3"
                  >
                    <GymMark name={g.name} imageUrl={g.image_url} size="md" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold">{g.name}</span>
                  </SurfaceCard>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label={t('ourGyms.setDefaultLabel')}
                    onPress={() => void handleSetDefault(g.id)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill={isDefault ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className={isDefault ? 'text-warning' : 'text-foreground/40'}
                    >
                      <path
                        d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 15.8 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </div>
              )
            })}
          </div>
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </Section>
      )}

      {/* A gym you have already asked to join. Listing it here is what keeps
            it out of the join list below, so nobody signs a document only to be
            told at the end that a request is already pending. */}
      {pending.length > 0 && (
        <Section title={t('ourGyms.pendingLabel')}>
          <div className="flex flex-col gap-2">
            {pending.map((r) => (
              <SurfaceCard
                key={r.gym_id}
                className="flex flex-row items-center gap-3 p-3 opacity-70"
              >
                <GymMark name={r.gym_name ?? '?'} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{r.gym_name}</p>
                  <span className="text-xs text-warning">{t('ourGyms.pendingBadge')}</span>
                </div>
              </SurfaceCard>
            ))}
          </div>
        </Section>
      )}

      {/* Join a new gym. GymDiscovery carries its own heading, so this one
            only earns its place as a separator below the gyms you already have. */}
      <Section title={gyms.length > 0 ? t('profile.joinNewGymLabel') : undefined}>
        <GymDiscovery excludeIds={[...gyms.map((g) => g.id), ...pending.map((r) => r.gym_id)]} />
      </Section>
    </Page>
  )
}
